import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTransactions, useUpdateTransaction, useBulkApproveTransactions } from "@/hooks/use-transactions";
import { formatSwissCurrency } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
// Icons now use Font Awesome (globally available)
import { TransactionDetailModal } from "./transaction-detail-modal";
import { TransactionReviewModal } from "./transaction-review-modal";
import { BulkOperationsPanel } from "./bulk-operations-panel";
import { AccountSelector } from "../validation/account-selector";
import { NoTransactionsEmptyState } from "@/components/empty-states";
import { createBookingFromTransaction, approveTransactionForBooking, rejectTransaction, batchApproveTransactions, batchRejectTransactions, batchBookTransactions } from "@/services/bookingService";
import { useToast } from "@/hooks/use-toast";
import type { TransactionFilters, TransactionRow } from "@/services/transactions";

interface DisplayTransaction {
  id: string;
  date: string;
  counterparty: string;
  amount: string;
  amountColor: string;
  amountRaw: number;
  debitAccount: string;
  debitAccountDisplay: string;
  creditAccount: string;
  creditAccountDisplay: string;
  vatRate: string;
  vatClass: string;
  confidence: number | null;
  confidenceDisplay: string;
  confidenceColor: string;
  borderColor: string;
  debitAccountStatus?: string;
  creditAccountStatus?: string;
  classificationSource?: string | null;
  classificationStatus: string;
}

interface TransactionGridProps {
  filters?: TransactionFilters;
  sortBy?: 'confidence' | 'date' | 'amount';
  onSelectionChange?: (ids: string[]) => void;
  onTransactionSelect?: (transaction: any) => void;
}

// Helper functions for formatting
const formatTransactionAmount = (amount: number) => {
  const formatted = formatSwissCurrency(Math.abs(amount));
  return amount >= 0 ? `+ ${formatted}` : `- ${formatted}`;
};

const getAmountColor = (amount: number) => {
  return amount >= 0 ? "text-brand-success" : "text-brand-danger";
};

const getVatRateDisplay = (vatRate: number | null) => {
  if (vatRate === null) return "0%";
  return `${vatRate}%`;
};

const getVatClass = (vatRate: number | null) => {
  if (!vatRate || vatRate === 0) return "bg-muted text-muted-foreground";
  if (vatRate === 2.5) return "bg-vat-reduced text-vat-reduced-text";
  if (vatRate === 3.7) return "bg-vat-accommodation text-vat-accommodation-text";
  if (vatRate === 7.7) return "bg-vat-standard text-vat-standard-text";
  return "bg-muted text-muted-foreground";
};

const getConfidenceColor = (confidence: number | null) => {
  if (!confidence) return "bg-gray-400";
  if (confidence >= 90) return "bg-green-500";
  if (confidence >= 70) return "bg-yellow-500";
  return "bg-red-500";
};

const getBorderColor = (confidence: number | null) => {
  if (!confidence) return "border-l-gray-400";
  if (confidence >= 90) return "border-l-green-400";
  if (confidence >= 70) return "border-l-yellow-400";
  return "border-l-red-400";
};

const getAccountStatus = (account: string | null) => {
  if (!account || account === "Uncategorized" || account.toLowerCase().includes("uncategorized")) {
    return "uncategorized";
  }
  return undefined;
};

const classificationStatusStyles = (status: string) => {
  switch (status) {
    case 'approved':
      return 'text-brand-success bg-brand-success-bg';
    case 'reviewed':
      return 'text-vat-standard-text bg-vat-standard';
    case 'flagged':
      return 'text-brand-danger bg-brand-danger-bg';
    default:
      return 'text-brand-warning bg-brand-warning-bg';
  }
};

const formatStatusLabel = (status: string) => {
  if (!status) return 'Pending';
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const formatSourceLabel = (source?: string | null) => {
  if (!source) return null;
  if (source === 'existing_rules') return 'Rule Engine';
  if (source === 'claude_sonnet_4_5') return 'Claude Sonnet 4.5';
  if (source === 'hybrid') return 'Hybrid AI';
  if (source === 'fallback') return 'Fallback Logic';
  const withSpaces = source.replace(/_/g, ' ');
  return withSpaces
    .split(' ')
    .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1))
    .join(' ');
};

export const TransactionGrid = ({ filters = {}, sortBy = 'confidence', onSelectionChange, onTransactionSelect }: TransactionGridProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTransaction, setSelectedTransaction] = useState<TransactionRow | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [editingAccountId, setEditingAccountId] = useState<string | null>(null);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [selectedTransactionForReview, setSelectedTransactionForReview] = useState<any>(null);
  const [creatingBookingId, setCreatingBookingId] = useState<string | null>(null);
  const [focusedRowIndex, setFocusedRowIndex] = useState<number>(-1);
  const rowRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const [selectedTransactionIds, setSelectedTransactionIds] = useState<string[]>([]);
  const [isBulkOperating, setIsBulkOperating] = useState(false);

  // Fetch transactions from database
  const {
    data: transactionData,
    isLoading,
    error,
    refetch
  } = useTransactions({
    page: currentPage,
    pageSize: 20,
    filters,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Debug logs removed for production

  const updateTransaction = useUpdateTransaction();
  const bulkApprove = useBulkApproveTransactions();

  // Store raw transactions for detail modal
  const rawTransactions = transactionData?.transactions || [];

  // Convert database transactions to display format - memoized for performance
  const transactions: DisplayTransaction[] = useMemo(() => {
    return rawTransactions.map((txn) => {
      // Transaction mapping
      const classification = txn.classification_payload;
      const accountName = typeof classification?.accountName === 'string' ? classification.accountName : undefined;

      const debitAccount = txn.debit_account || 'Uncategorized';
      const creditAccount = txn.credit_account || 'Uncategorized';
      const isExpense = txn.amount < 0;

      const debitAccountDisplay = isExpense && accountName
        ? `${debitAccount} · ${accountName}`
        : debitAccount;

      const creditAccountDisplay = !isExpense && accountName
        ? `${creditAccount} · ${accountName}`
        : creditAccount;

      const confidenceScoreRaw = typeof txn.ai_confidence_score === 'number'
        ? Math.max(0, Math.min(100, txn.ai_confidence_score))
        : null;
      const confidenceScoreRounded = confidenceScoreRaw != null ? Math.round(confidenceScoreRaw) : null;
      const confidenceDisplay = confidenceScoreRounded != null ? `${confidenceScoreRounded}%` : 'N/A';

      return {
        id: txn.id,
        date: new Date(txn.transaction_date).toISOString().split('T')[0],
        counterparty: txn.counterparty_name || txn.description || 'Unknown',
        amount: formatTransactionAmount(txn.amount),
        amountColor: getAmountColor(txn.amount),
        amountRaw: txn.amount,
        debitAccount,
        debitAccountDisplay,
        creditAccount,
        creditAccountDisplay,
        vatRate: getVatRateDisplay(txn.swiss_vat_rate),
        vatClass: getVatClass(txn.swiss_vat_rate),
        confidence: confidenceScoreRaw,
        confidenceDisplay,
        confidenceColor: getConfidenceColor(confidenceScoreRaw),
        borderColor: getBorderColor(confidenceScoreRaw),
        debitAccountStatus: getAccountStatus(debitAccount),
        creditAccountStatus: getAccountStatus(creditAccount),
        classificationSource: txn.classification_source ?? classification?.source ?? null,
        classificationStatus: txn.classification_status,
      };
    });
  }, [rawTransactions]);

  // Apply client-side sorting - memoized for performance
  const sortedTransactions = useMemo(() => {
    return [...transactions].sort((a, b) => {
      switch (sortBy) {
        case 'confidence':
          const aConf = a.confidence ?? -1;
          const bConf = b.confidence ?? -1;
          return bConf - aConf; // Higher confidence first
        case 'date':
          return new Date(b.date).getTime() - new Date(a.date).getTime(); // Newer first
        case 'amount':
          return Math.abs(b.amountRaw) - Math.abs(a.amountRaw); // Larger amount first
        default:
          return 0;
      }
    });
  }, [transactions, sortBy]);

  // Mapped transactions ready for display

  const handleSelectAll = () => {
    const newSelected = selectAll 
      ? new Set<string>() 
      : new Set(sortedTransactions.map(t => t.id));
    setSelectedRows(newSelected);
    setSelectAll(!selectAll);
    onSelectionChange?.(Array.from(newSelected));
  };

  const handleRowSelect = useCallback((id: string) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
    setSelectAll(newSelected.size === sortedTransactions.length);
    onSelectionChange?.(Array.from(newSelected));
  }, [selectedRows, sortedTransactions.length, onSelectionChange]);

  const handleBulkApprove = useCallback(async () => {
    if (selectedRows.size > 0) {
      await bulkApprove.mutateAsync(Array.from(selectedRows));
      setSelectedRows(new Set());
      setSelectAll(false);
    }
  }, [selectedRows, bulkApprove]);

  const handleTransactionClick = useCallback((rawTxn: TransactionRow) => {
    setSelectedTransaction(rawTxn);
    setDetailModalOpen(true);
    onTransactionSelect?.(rawTxn);
  }, [onTransactionSelect]);

  const handleSaveTransaction = (updates: any) => {
    updateTransaction.mutate({
      transactionId: updates.id,
      updates: {
        account_number: updates.account_number,
        human_verified: updates.human_verified,
        needs_review: updates.needs_review,
      }
    });
    setDetailModalOpen(false);
  };

  const handleCreateBooking = async (transactionId: string) => {
    setCreatingBookingId(transactionId);
    try {
      const result = await createBookingFromTransaction(transactionId);
      toast({
        title: "Booking Created",
        description: `Journal Entry #${result.entry_number} created successfully`,
      });
      refetch(); // Refresh transaction list
    } catch (error: any) {
      toast({
        title: "Booking Failed",
        description: error.message || "Failed to create booking",
        variant: "destructive",
      });
    } finally {
      setCreatingBookingId(null);
    }
  };

  // Bulk operations handlers
  const handleBulkApproveBatch = async () => {
    setIsBulkOperating(true);
    try {
      const result = await batchApproveTransactions(selectedTransactionIds);
      toast({
        title: "Bulk-Genehmigung abgeschlossen",
        description: `${result.success} Transaktionen genehmigt${result.failed > 0 ? `, ${result.failed} fehlgeschlagen` : ''}`,
      });
      if (result.errors.length > 0) {
        console.error('Bulk approve errors:', result.errors);
      }
      refetch();
      setSelectedTransactionIds([]);
    } catch (error) {
      toast({
        title: "Fehler",
        description: "Fehler bei der Bulk-Genehmigung",
        variant: "destructive",
      });
    } finally {
      setIsBulkOperating(false);
    }
  };

  const handleBulkRejectBatch = async () => {
    setIsBulkOperating(true);
    try {
      const result = await batchRejectTransactions(selectedTransactionIds);
      toast({
        title: "Bulk-Ablehnung abgeschlossen",
        description: `${result.success} Transaktionen abgelehnt${result.failed > 0 ? `, ${result.failed} fehlgeschlagen` : ''}`,
      });
      if (result.errors.length > 0) {
        console.error('Bulk reject errors:', result.errors);
      }
      refetch();
      setSelectedTransactionIds([]);
    } catch (error) {
      toast({
        title: "Fehler",
        description: "Fehler bei der Bulk-Ablehnung",
        variant: "destructive",
      });
    } finally {
      setIsBulkOperating(false);
    }
  };

  const handleBulkBookBatch = async () => {
    setIsBulkOperating(true);
    try {
      const result = await batchBookTransactions(selectedTransactionIds);
      toast({
        title: "Bulk-Buchung abgeschlossen",
        description: `${result.success} Transaktionen gebucht${result.failed > 0 ? `, ${result.failed} fehlgeschlagen` : ''}`,
      });
      if (result.errors.length > 0) {
        console.error('Bulk booking errors:', result.errors);
      }
      refetch();
      setSelectedTransactionIds([]);
    } catch (error) {
      toast({
        title: "Fehler",
        description: "Fehler bei der Bulk-Buchung",
        variant: "destructive",
      });
    } finally {
      setIsBulkOperating(false);
    }
  };

  const toggleTransactionSelection = (transactionId: string) => {
    setSelectedTransactionIds(prev => 
      prev.includes(transactionId) 
        ? prev.filter(id => id !== transactionId)
        : [...prev, transactionId]
    );
  };

  const selectAllVisible = () => {
    const visibleIds = sortedTransactions.map(t => t.id);
    setSelectedTransactionIds(visibleIds);
  };

  const clearSelection = () => {
    setSelectedTransactionIds([]);
  };

  // Enhanced keyboard navigation with bulk operations
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if user is typing in input field
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      const maxIndex = sortedTransactions.length - 1;
      
      // Handle Ctrl/Cmd + key combinations
      if (e.ctrlKey || e.metaKey) {
        switch (e.key.toLowerCase()) {
          case 'a':
            e.preventDefault();
            // Select all visible transactions
            const allIds = sortedTransactions.map(t => t.id);
            setSelectedRows(new Set(allIds));
            setSelectAll(true);
            onSelectionChange?.(allIds);
            toast({
              title: 'Alle ausgewählt',
              description: `${allIds.length} Transaktionen ausgewählt`,
            });
            break;
          case 'd':
            e.preventDefault();
            // Deselect all
            setSelectedRows(new Set());
            setSelectAll(false);
            onSelectionChange?.([]);
            toast({
              title: 'Auswahl aufgehoben',
              description: 'Alle Transaktionen abgewählt',
            });
            break;
          case 'e':
            e.preventDefault();
            // Edit focused transaction
            if (focusedRowIndex >= 0 && focusedRowIndex <= maxIndex) {
              const transaction = sortedTransactions[focusedRowIndex];
              setEditingAccountId(transaction.id);
            }
            break;
        }
        return;
      }
      
      // Handle single key presses
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusedRowIndex(prev => {
            const next = Math.min(maxIndex, prev + 1);
            // Scroll into view
            setTimeout(() => {
              rowRefs.current.get(next)?.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'nearest' 
              });
            }, 0);
            return next;
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedRowIndex(prev => {
            const next = Math.max(0, prev - 1);
            setTimeout(() => {
              rowRefs.current.get(next)?.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'nearest' 
              });
            }, 0);
            return next;
          });
          break;
        case 'Enter':
          if (focusedRowIndex >= 0 && focusedRowIndex <= maxIndex) {
            e.preventDefault();
            const transaction = sortedTransactions[focusedRowIndex];
            const rawTxn = rawTransactions.find(t => t.id === transaction.id);
            if (rawTxn) handleTransactionClick(rawTxn);
          }
          break;
        case 'Escape':
          setFocusedRowIndex(-1);
          setEditingAccountId(null);
          break;
        case ' ':
          // Space bar to toggle selection of focused row
          if (focusedRowIndex >= 0 && focusedRowIndex <= maxIndex) {
            e.preventDefault();
            const transaction = sortedTransactions[focusedRowIndex];
            handleRowSelect(transaction.id);
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [sortedTransactions, focusedRowIndex, rawTransactions]);

  return (
    <section className="space-y-2">
      {/* Error State */}
      {error && (
        <Alert className="border-destructive bg-destructive/10">
          <i className="fa-solid fa-triangle-exclamation text-sm text-destructive"></i>
          <AlertDescription className="flex items-center justify-between">
            <span>Failed to load transactions: {error.message}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              className="ml-4"
            >
              <i className="fa-solid fa-rotate-right mr-2 text-sm"></i>
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Bulk Operations Panel */}
      {selectedTransactionIds.length > 0 && (
        <BulkOperationsPanel
          selectedIds={selectedTransactionIds}
          transactions={rawTransactions}
          onBulkApprove={handleBulkApproveBatch}
          onBulkReject={handleBulkRejectBatch}
          onBulkBook={handleBulkBookBatch}
          onClearSelection={clearSelection}
          isLoading={isBulkOperating}
        />
      )}

      {/* Grid Header - Sticky */}
      <div className="sticky top-0 z-10 px-4 py-2 bg-brand-secondary rounded-lg flex items-center gap-4 text-xs font-medium text-brand-text-secondary shadow-sm">
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-gray-300 text-brand-primary focus:ring-brand-primary"
          checked={selectedTransactionIds.length === sortedTransactions.length && sortedTransactions.length > 0}
          onChange={(e) => {
            if (e.target.checked) {
              selectAllVisible();
            } else {
              clearSelection();
            }
          }}
          disabled={isLoading}
        />
        <div className="w-[9%] cursor-pointer hover:text-brand-primary">Date <i className="fa-solid fa-sort ml-1"></i></div>
        <div className="w-[16%] cursor-pointer hover:text-brand-primary">Counterparty <i className="fa-solid fa-sort ml-1"></i></div>
        <div className="w-[13%] cursor-pointer hover:text-brand-primary">Amount <i className="fa-solid fa-sort ml-1"></i></div>
        <div className="w-[13%] cursor-pointer hover:text-brand-primary">Debit Account <i className="fa-solid fa-sort ml-1"></i></div>
        <div className="w-[13%] cursor-pointer hover:text-brand-primary">Credit Account <i className="fa-solid fa-sort ml-1"></i></div>
        <div className="w-[8%] cursor-pointer hover:text-brand-primary">VAT Rate <i className="fa-solid fa-sort ml-1"></i></div>
        <div className="w-[10%] cursor-pointer hover:text-brand-primary">AI Confidence <i className="fa-solid fa-sort-down text-brand-primary"></i></div>
        <div className="w-[10%]">Actions</div>
      </div>

      {/* Transaction Rows */}
      {isLoading ? (
        // Loading skeleton rows
        Array.from({ length: 5 }).map((_, index) => (
          <div key={index} className="bg-white p-4 rounded-xl border shadow-sm flex items-center gap-4">
            <Skeleton className="h-4 w-4" />
            <Skeleton className="w-[10%] h-4" />
            <Skeleton className="w-[18%] h-4" />
            <Skeleton className="w-[15%] h-4" />
            <Skeleton className="w-[15%] h-6" />
            <Skeleton className="w-[15%] h-6" />
            <Skeleton className="w-[10%] h-6" />
            <div className="w-[12%] flex items-center gap-2">
              <Skeleton className="flex-1 h-2" />
              <Skeleton className="w-8 h-4" />
            </div>
          </div>
        ))
      ) : sortedTransactions.length === 0 ? (
        // Professional empty state for onboarding
        <NoTransactionsEmptyState />
      ) : (
        // Real transaction rows
        sortedTransactions.map((transaction, index) => {
          const rawTxn = rawTransactions.find(t => t.id === transaction.id);
          const isFocused = focusedRowIndex === index;
          return (
            <div
              key={transaction.id}
              ref={(el) => {
                if (el) rowRefs.current.set(index, el);
                else rowRefs.current.delete(index);
              }}
              className={`bg-white p-4 rounded-xl border shadow-sm flex items-center gap-4 text-sm transition-all duration-200 border-l-4 ${transaction.borderColor} cursor-pointer ${
                isFocused 
                  ? 'border-brand-accent ring-2 ring-brand-accent ring-offset-2' 
                  : 'border-transparent hover:border-brand-accent'
              }`}
              onClick={() => rawTxn && handleTransactionClick(rawTxn)}
              onDoubleClick={() => setEditingAccountId(transaction.id)}
            >
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-brand-primary focus:ring-brand-primary"
                checked={selectedTransactionIds.includes(transaction.id)}
                onChange={(e) => {
                  e.stopPropagation();
                  toggleTransactionSelection(transaction.id);
                }}
                onClick={(e) => e.stopPropagation()}
              />
            <div className="w-[9%] text-xs">{transaction.date}</div>
            <div className="w-[16%] truncate" title={transaction.counterparty}>
              <div className="truncate text-sm font-medium text-brand-text-primary">
                {transaction.counterparty}
              </div>
              <div className="mt-1 flex items-center gap-2 text-[10px]">
                <span className={`px-2 py-[2px] rounded-full font-semibold ${classificationStatusStyles(transaction.classificationStatus)}`}>
                  {formatStatusLabel(transaction.classificationStatus)}
                </span>
                {transaction.classificationSource && (
                  <span className="text-[10px] text-brand-text-secondary font-semibold">
                    {formatSourceLabel(transaction.classificationSource)}
                  </span>
                )}
              </div>
            </div>
            <div className={`w-[13%] font-medium ${transaction.amountColor}`}>
              {transaction.amount}
            </div>
            <div className="w-[13%]" onClick={(e) => e.stopPropagation()}>
              {editingAccountId === transaction.id ? (
                <div className="flex items-center gap-1">
                  <div className="min-w-[200px]">
                    <AccountSelector 
                      value={transaction.debitAccount}
                      onChange={(account) => {
                        updateTransaction.mutate({
                          transactionId: transaction.id,
                          updates: {
                            account_number: account.split(' - ')[0],
                            needs_review: false,
                            human_verified: true
                          }
                        });
                        setEditingAccountId(null);
                        toast({
                          title: 'Konto korrigiert',
                          description: 'Transaktion wurde erfolgreich aktualisiert',
                        });
                      }}
                      placeholder="Konto auswählen"
                    />
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingAccountId(null);
                    }}
                    className="p-1 text-xs text-gray-500 hover:text-gray-700"
                    title="Abbrechen"
                  >
                    <i className="fa-solid fa-times"></i>
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-1">
                  <span 
                    className={`px-2 py-1 rounded text-xs cursor-pointer hover:bg-gray-200 transition-colors ${
                      transaction.debitAccountStatus === 'uncategorized'
                        ? 'bg-yellow-100 text-yellow-800 border border-yellow-300'
                        : 'bg-gray-100 text-gray-800'
                    }`} 
                    title={`${transaction.debitAccountDisplay} - Doppelklick zum Bearbeiten`}
                    onDoubleClick={() => setEditingAccountId(transaction.id)}
                  >
                    {transaction.debitAccountDisplay}
                  </span>
                  {transaction.debitAccountStatus === 'uncategorized' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingAccountId(transaction.id);
                      }}
                      className="px-2 py-1 bg-brand-accent text-white rounded text-xs hover:bg-brand-primary transition-colors"
                      title="Konto korrigieren"
                    >
                      <i className="fa-solid fa-pen"></i>
                    </button>
                  )}
                  {/* Quick Edit Button for All Transactions */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingAccountId(transaction.id);
                    }}
                    className="px-1 py-1 text-gray-400 hover:text-gray-600 transition-colors"
                    title="Konto bearbeiten (Strg+E)"
                  >
                    <i className="fa-solid fa-edit text-xs"></i>
                  </button>
                </div>
              )}
            </div>
            <div className="w-[13%]" onClick={(e) => e.stopPropagation()}>
              {editingAccountId === transaction.id ? (
                <div className="flex items-center gap-1">
                  <div className="min-w-[200px]">
                    <AccountSelector 
                      value={transaction.creditAccount}
                      onChange={(account) => {
                        updateTransaction.mutate({
                          transactionId: transaction.id,
                          updates: {
                            account_number: account.split(' - ')[0],
                            needs_review: false,
                            human_verified: true
                          }
                        });
                        setEditingAccountId(null);
                        toast({
                          title: 'Credit Account korrigiert',
                          description: 'Transaktion wurde erfolgreich aktualisiert',
                        });
                      }}
                      placeholder="Credit Account auswählen"
                    />
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingAccountId(null);
                    }}
                    className="p-1 text-xs text-gray-500 hover:text-gray-700"
                    title="Abbrechen"
                  >
                    <i className="fa-solid fa-times"></i>
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-1">
                  <span 
                    className={`px-2 py-1 rounded text-xs cursor-pointer hover:bg-gray-200 transition-colors ${
                      transaction.creditAccountStatus === 'uncategorized'
                        ? 'bg-yellow-100 text-yellow-800 border border-yellow-300'
                        : 'bg-gray-100 text-gray-800'
                    }`} 
                    title={`${transaction.creditAccountDisplay} - Doppelklick zum Bearbeiten`}
                    onDoubleClick={() => setEditingAccountId(transaction.id)}
                  >
                    {transaction.creditAccountDisplay}
                  </span>
                  {transaction.creditAccountStatus === 'uncategorized' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingAccountId(transaction.id);
                      }}
                      className="px-2 py-1 bg-brand-accent text-white rounded text-xs hover:bg-brand-primary transition-colors"
                      title="Credit Account korrigieren"
                    >
                      <i className="fa-solid fa-pen"></i>
                    </button>
                  )}
                  {/* Quick Edit Button for Credit Account */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingAccountId(transaction.id);
                    }}
                    className="px-1 py-1 text-gray-400 hover:text-gray-600 transition-colors"
                    title="Credit Account bearbeiten (Strg+E)"
                  >
                    <i className="fa-solid fa-edit text-xs"></i>
                  </button>
                </div>
              )}
            </div>
            <div className="w-[8%]">
              <span className={`px-2 py-1 text-xs font-medium rounded-md ${transaction.vatClass}`}>
                {transaction.vatRate}
              </span>
            </div>
            <div className="w-[10%] flex items-center gap-2">
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div
                  className={`${transaction.confidenceColor} h-1.5 rounded-full transition-all duration-300`}
                  style={{ width: `${transaction.confidence != null ? Math.min(100, Math.max(0, transaction.confidence)) : 0}%` }}
                ></div>
              </div>
              <span className="text-xs font-bold">{transaction.confidenceDisplay}</span>
            </div>
            <div className="w-[10%] flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
              {(rawTxn as any)?.journal_entry_id ? (
                <button
                  className="px-2 py-1 bg-brand-success-bg text-brand-success rounded text-xs flex items-center gap-1"
                  title="Already booked"
                  disabled
                >
                  <i className="fa-solid fa-circle-check text-xs"></i>
                  Gebucht
                </button>
              ) : (
                <>
                  {/* Review Status Badge */}
                  {(rawTxn as any)?.review_status && (
                    <span className={`px-2 py-1 text-xs rounded ${
                      (rawTxn as any).review_status === 'approved' ? 'bg-green-100 text-green-800' :
                      (rawTxn as any).review_status === 'rejected' ? 'bg-red-100 text-red-800' :
                      (rawTxn as any).review_status === 'flagged' ? 'bg-orange-100 text-orange-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {(rawTxn as any).review_status === 'approved' ? '✓' :
                       (rawTxn as any).review_status === 'rejected' ? '✗' :
                       (rawTxn as any).review_status === 'flagged' ? '⚠️' : '⏳'}
                    </span>
                  )}
                  
                  {/* Review Button */}
                  {(!(rawTxn as any)?.booking_approved) && (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        setSelectedTransactionForReview(transaction);
                        setReviewModalOpen(true);
                      }}
                      className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200 transition-colors flex items-center gap-1"
                      title="Transaktion überprüfen"
                    >
                      <i className="fa-solid fa-clipboard-check text-xs"></i>
                      Review
                    </button>
                  )}
                  
                  {/* Book Button (only if approved) */}
                  {(rawTxn as any)?.booking_approved && (
                    <button
                      onClick={() => handleCreateBooking(transaction.id)}
                      disabled={creatingBookingId === transaction.id}
                      className="px-2 py-1 bg-brand-accent text-white rounded text-xs hover:bg-brand-primary transition-colors disabled:opacity-50 flex items-center gap-1"
                      title="Swiss GAAP Buchung erstellen"
                    >
                      <i className="fa-solid fa-book-open text-xs"></i>
                      {creatingBookingId === transaction.id ? 'Erstelle...' : 'Buchen'}
                    </button>
                  )}
                  
                  {/* Bezugsteuer Indicator */}
                  {(rawTxn as any)?.requires_bezugsteuer && (
                    <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded text-xs" title="Bezugsteuer erforderlich">
                      <i className="fa-solid fa-globe"></i>
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
        );
        })
      )}

      {/* Transaction Review Modal */}
      <TransactionReviewModal
        open={reviewModalOpen}
        onOpenChange={setReviewModalOpen}
        transaction={selectedTransactionForReview}
        onApprove={async () => {
          try {
            await approveTransactionForBooking(selectedTransactionForReview.id);
            toast({
              title: 'Transaktion genehmigt',
              description: 'Die Transaktion kann jetzt gebucht werden.',
            });
            // Refresh data
            window.location.reload();
          } catch (error: any) {
            toast({
              title: 'Fehler',
              description: error.message,
              variant: 'destructive',
            });
          }
        }}
        onReject={async (reason) => {
          try {
            await rejectTransaction(selectedTransactionForReview.id, reason);
            toast({
              title: 'Transaktion abgelehnt',
              description: 'Die Transaktion wurde zur Überarbeitung markiert.',
            });
            window.location.reload();
          } catch (error: any) {
            toast({
              title: 'Fehler',
              description: error.message,
              variant: 'destructive',
            });
          }
        }}
      />

      {/* Pagination Info */}
      {transactionData && transactionData.totalCount > 0 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground mt-4">
          <span>
            Showing {sortedTransactions.length} of {transactionData.totalCount} transactions
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={!transactionData.hasPreviousPage || isLoading}
            >
              Previous
            </Button>
            <span>
              Page {transactionData.currentPage} of {transactionData.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => prev + 1)}
              disabled={!transactionData.hasNextPage || isLoading}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <TransactionDetailModal
        transaction={selectedTransaction}
        open={detailModalOpen}
        onOpenChange={setDetailModalOpen}
        onSave={handleSaveTransaction}
      />
    </section>
  );
};
